import React, { useState, useEffect, useCallback, useRef } from 'react';
import { bookmarksApi } from '../../api/bookmarks';
import { bookmarkFoldersApi } from '../../api/bookmarkFolders';
import BookmarkListItem from './BookmarkListItem';
import BookmarkFolderItem from './BookmarkFolderItem';
import CreateBookmarkFolderButton from './CreateBookmarkFolderButton';
import CreateSynthesis from './CreateSynthesis';
import Loading from '../Common/Loading';
import './Bookmarks.css';

// A sidebar read that never settles used to leave "Loading..." up.
// Same window as an ordinary API read. Past that, show the existing error.
export const BOOKMARKS_SIDEBAR_TIMEOUT_MS = 30000;

function waitForSidebar(work, signal) {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      const aborted = new Error('aborted');
      aborted.name = 'AbortError';
      reject(aborted);
      return;
    }
    let settled = false;
    const finish = (fn, value) => {
      if (settled) return;
      settled = true;
      signal?.removeEventListener('abort', onAbort);
      fn(value);
    };
    const onAbort = () => {
      const aborted = new Error('aborted');
      aborted.name = 'AbortError';
      finish(reject, aborted);
    };
    signal?.addEventListener('abort', onAbort);
    Promise.resolve()
      .then(work)
      .then((value) => finish(resolve, value))
      .catch((err) => finish(reject, err));
  });
}

const CATEGORIES = [
  { value: null, label: 'All' },
  { value: 'character', label: 'Characters', color: '#3B82F6' },
  { value: 'plot', label: 'Plot', color: '#10B981' },
  { value: 'setting', label: 'Setting', color: '#8B5CF6' },
  { value: 'theme', label: 'Themes', color: '#F59E0B' }
];

// Local storage key for folder expanded state
const FOLDER_EXPANDED_KEY = 'brainstormy.bookmarkFolders.expanded';

function asRootBookmark(bookmark) {
  return {
    type: 'bookmark',
    id: bookmark.id,
    name: bookmark.name,
    content: bookmark.content,
    category: bookmark.category,
    bookmark_type: bookmark.bookmark_type,
    session_name: bookmark.session_name,
    created_at: bookmark.created_at,
    position: null,
    folder_id: null,
  };
}

function asFolderBookmark(bookmark) {
  return {
    id: bookmark.id,
    name: bookmark.name,
    content: bookmark.content,
    category: bookmark.category,
    bookmark_type: bookmark.bookmark_type,
    session_name: bookmark.session_name,
    created_at: bookmark.created_at,
  };
}

// Pull the bookmark out of the root list or its current folder and put it
// at the end of the destination. folderId null is the top-level list.
export function relocateBookmark(items, bookmarkId, folderId) {
  let moved = null;
  const next = [];
  for (const item of items) {
    if (item.type === 'bookmark' && item.id === bookmarkId) {
      moved = item;
      continue;
    }
    if (item.type === 'folder') {
      const bookmarks = [];
      for (const bookmark of item.bookmarks || []) {
        if (bookmark.id === bookmarkId) moved = { ...bookmark, type: 'bookmark' };
        else bookmarks.push(bookmark);
      }
      next.push({ ...item, bookmarks, bookmark_count: bookmarks.length });
      continue;
    }
    next.push(item);
  }
  if (!moved) return items;
  if (!folderId) {
    next.push(asRootBookmark(moved));
    return next;
  }
  return next.map((item) => {
    if (item.type !== 'folder' || item.id !== folderId) return item;
    const bookmarks = [...(item.bookmarks || []), asFolderBookmark(moved)];
    return { ...item, bookmarks, bookmark_count: bookmarks.length };
  });
}

export function renameFolderInItems(items, folderId, name) {
  return items.map((item) => (
    item.type === 'folder' && item.id === folderId
      ? { ...item, name }
      : item
  ));
}

export function removeBookmark(items, bookmarkId) {
  return items
    .filter((item) => !(item.type === 'bookmark' && item.id === bookmarkId))
    .map((item) => {
      if (item.type !== 'folder' || !item.bookmarks) return item;
      if (!item.bookmarks.some((bookmark) => bookmark.id === bookmarkId)) return item;
      const bookmarks = item.bookmarks.filter((bookmark) => bookmark.id !== bookmarkId);
      return { ...item, bookmarks, bookmark_count: bookmarks.length };
    });
}

export function deleteFolderToRoot(items, folderId) {
  const folder = items.find((item) => item.type === 'folder' && item.id === folderId);
  const lifted = (folder?.bookmarks || []).map((bookmark) => ({
    type: 'bookmark',
    ...bookmark,
    folder_id: null,
  }));
  return [
    ...items.filter((item) => !(item.type === 'folder' && item.id === folderId)),
    ...lifted,
  ];
}

export function deleteFolderAndBookmarks(items, folderId) {
  return items.filter((item) => !(item.type === 'folder' && item.id === folderId));
}

function swapPositions(list, from, to) {
  if (from === to || from < 0 || to < 0 || from >= list.length || to >= list.length) return list;
  const next = list.slice();
  [next[from], next[to]] = [next[to], next[from]];
  return next;
}

export function reorderBookmarkInItems(items, bookmarkId, direction) {
  const rootPositions = [];
  items.forEach((item, index) => {
    if (item.type === 'bookmark') rootPositions.push(index);
  });
  const pos = rootPositions.findIndex((index) => items[index].id === bookmarkId);
  if (pos !== -1) {
    const target = direction === 'up' ? pos - 1 : pos + 1;
    if (target < 0 || target >= rootPositions.length) return items;
    return swapPositions(items, rootPositions[pos], rootPositions[target]);
  }
  return items.map((item) => {
    if (item.type !== 'folder') return item;
    const bookmarks = item.bookmarks || [];
    const index = bookmarks.findIndex((bookmark) => bookmark.id === bookmarkId);
    if (index === -1) return item;
    const target = direction === 'up' ? index - 1 : index + 1;
    if (target < 0 || target >= bookmarks.length) return item;
    return { ...item, bookmarks: swapPositions(bookmarks, index, target) };
  });
}

export function reorderFolderInItems(items, folderId, direction) {
  const positions = [];
  items.forEach((item, index) => {
    if (item.type === 'folder') positions.push(index);
  });
  const pos = positions.findIndex((index) => items[index].id === folderId);
  const target = direction === 'up' ? pos - 1 : pos + 1;
  if (pos === -1 || target < 0 || target >= positions.length) return items;
  return swapPositions(items, positions[pos], positions[target]);
}

// Neighbor id for POST /bookmark-folders/{id}/reorder. Move up sends that
// folder as before_id. Move down sends it as after_id.
export function folderMoveIds(items, folderId, direction) {
  const folders = items.filter((item) => item.type === 'folder');
  const index = folders.findIndex((folder) => folder.id === folderId);
  if (index === -1) return null;
  const neighbor = direction === 'up' ? folders[index - 1] : folders[index + 1];
  if (!neighbor) return null;
  return direction === 'up'
    ? { afterId: null, beforeId: neighbor.id }
    : { afterId: neighbor.id, beforeId: null };
}

/**
 * BookmarksView — bookmark content (category filters, folders, list, synthesis).
 *
 * The chrome-free body of the bookmarks feature. Rendered both as a tab inside the
 * Compendium panel (the canonical home, bible-consolidation) and, historically, by
 * the standalone BookmarksPanel wrapper. Preserves the full folder structure and
 * message-anchoring — it does not flatten bookmarks to a plain list.
 */
function BookmarksView({ storyId, projectId }) {
  const [sidebarItems, setSidebarItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeCategory, setActiveCategory] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [actionError, setActionError] = useState(null);
  const [expandedFolders, setExpandedFolders] = useState(() => {
    try {
      const stored = localStorage.getItem(FOLDER_EXPANDED_KEY);
      return stored ? JSON.parse(stored) : {};
    } catch {
      return {};
    }
  });

  // Determine if we're in story or project context
  const isProjectContext = !storyId && projectId;
  const containerType = isProjectContext ? 'project' : 'story';
  const containerId = isProjectContext ? projectId : storyId;

  // Save expanded folders to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(FOLDER_EXPANDED_KEY, JSON.stringify(expandedFolders));
    } catch (e) {
      console.warn('Failed to save folder state:', e);
    }
  }, [expandedFolders]);

  const loadGenerationRef = useRef(0);
  const activeLoadRef = useRef(null);
  const sidebarRef = useRef(sidebarItems);
  const actionGen = useRef(0);
  const skipRemovedReload = useRef(0);

  // Same event a single bookmark delete sends. The chat drops that pin
  // and reads the bookmark list again with no-store.
  const releaseBookmarkPins = (ids) => {
    const list = (Array.isArray(ids) ? ids : [])
      .map((id) => (id == null ? '' : String(id)))
      .filter((id) => id.length > 0);
    if (!list.length) return;
    skipRemovedReload.current = list.length;
    list.forEach((bookmarkId) => {
      window.dispatchEvent(new CustomEvent('bookmark-removed', {
        detail: { bookmarkId },
      }));
    });
  };

  useEffect(() => {
    sidebarRef.current = sidebarItems;
  }, [sidebarItems]);

  const commitItems = (next) => {
    sidebarRef.current = next;
    setSidebarItems(next);
  };

  // Show the next list immediately, then save. A later edit wins over an
  // older failure so a slow error cannot put the older list back.
  const runMutation = async (produceNext, request, message) => {
    const generation = ++actionGen.current;
    const previous = sidebarRef.current;
    const next = produceNext(previous);
    if (next === previous) return;
    commitItems(next);
    setActionError(null);
    try {
      await request();
    } catch (err) {
      console.error(message, err);
      if (actionGen.current === generation) commitItems(previous);
      setActionError(message);
    }
  };

  // Load sidebar data (folders + bookmarks). Category filtering is local,
  // so a filter click must not start another read.
  const loadSidebar = useCallback(async ({ quiet = false } = {}) => {
    activeLoadRef.current?.abort();
    const controller = new AbortController();
    activeLoadRef.current = controller;
    const generation = ++loadGenerationRef.current;
    const stillCurrent = () => generation === loadGenerationRef.current;
    const signal = controller.signal;
    let timedOut = false;
    const timer = setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, BOOKMARKS_SIDEBAR_TIMEOUT_MS);

    if (!storyId && !projectId) {
      clearTimeout(timer);
      sidebarRef.current = [];
      setSidebarItems([]);
      setIsLoading(false);
      return;
    }

    if (!quiet) setIsLoading(true);
    setError(null);
    try {
      const data = await waitForSidebar(() => (
        isProjectContext
          ? bookmarksApi.getProjectSidebar(projectId, { signal })
          : bookmarksApi.getStorySidebar(storyId, { signal })
      ), signal);
      if (!stillCurrent()) return;
      sidebarRef.current = data?.items || [];
      setSidebarItems(data?.items || []);
    } catch (err) {
      if (!stillCurrent()) return;
      if (err?.name === 'AbortError' && !timedOut) return;
      // If sidebar endpoint doesn't exist yet (migrations not run), fall back to list
      if (!timedOut && (err.message?.includes('404') || err.message?.includes('Not Found'))) {
        try {
          const bookmarks = await waitForSidebar(() => (
            isProjectContext
              ? bookmarksApi.listProject(projectId, null, { signal })
              : bookmarksApi.list(storyId, null, { signal })
          ), signal);
          if (!stillCurrent()) return;
          const rows = Array.isArray(bookmarks) ? bookmarks : [];
          const fallbackItems = rows.map(b => ({
            type: 'bookmark',
            id: b.id,
            name: b.user_title,
            content: b.content,
            category: b.category,
            bookmark_type: b.is_synthesis ? 'synthesis' : 'message',
            session_name: b.session_name,
            created_at: b.created_at,
            position: null,
            folder_id: null
          }));
          sidebarRef.current = fallbackItems;
          setSidebarItems(fallbackItems);
        } catch (fallbackErr) {
          if (!stillCurrent()) return;
          if (fallbackErr?.name === 'AbortError' && !timedOut) return;
          setError(fallbackErr.message || 'Failed to load bookmarks');
        }
      } else {
        setError(timedOut ? 'Failed to load bookmarks' : (err.message || 'Failed to load bookmarks'));
      }
    } finally {
      clearTimeout(timer);
      if (stillCurrent() && (timedOut || !signal.aborted)) setIsLoading(false);
    }
  }, [storyId, projectId, isProjectContext]);

  useEffect(() => {
    loadSidebar();
    return () => {
      loadGenerationRef.current += 1;
      activeLoadRef.current?.abort();
    };
  }, [loadSidebar]);

  // Chat pin and the list's own delete both notify this view.
  useEffect(() => {
    const onCreated = () => {
      loadSidebar({ quiet: true });
    };
    const onRemoved = () => {
      if (skipRemovedReload.current > 0) {
        skipRemovedReload.current -= 1;
        return;
      }
      loadSidebar({ quiet: true });
    };
    window.addEventListener('bookmark-created', onCreated);
    window.addEventListener('bookmark-removed', onRemoved);
    return () => {
      window.removeEventListener('bookmark-created', onCreated);
      window.removeEventListener('bookmark-removed', onRemoved);
    };
  }, [loadSidebar]);

  // Toggle folder expanded state
  const handleToggleFolderCollapse = (folderId) => {
    setExpandedFolders(prev => ({
      ...prev,
      [folderId]: !prev[folderId]
    }));
  };

  // The name shows at once. The save replaces the temporary id, or the
  // folder comes back off the list if the save fails.
  const handleFolderCreated = async (name) => {
    const tempId = `pending-folder-${Date.now()}-${Math.random().toString(16).slice(2)}`;
    const generation = ++actionGen.current;
    const previous = sidebarRef.current;
    commitItems([
      ...previous,
      {
        type: 'folder',
        id: tempId,
        name,
        position: null,
        is_collapsed: false,
        bookmark_count: 0,
        bookmarks: [],
      },
    ]);
    setExpandedFolders((prev) => ({ ...prev, [tempId]: true }));
    setActionError(null);
    try {
      const created = containerType === 'project'
        ? await bookmarkFoldersApi.createProject(containerId, name)
        : await bookmarkFoldersApi.createStory(containerId, name);
      const current = sidebarRef.current;
      if (current.some((item) => item.type === 'folder' && item.id === tempId)) {
        commitItems(current.map((item) => (
          item.type === 'folder' && item.id === tempId
            ? {
              type: 'folder',
              name,
              bookmark_count: 0,
              ...(created || {}),
              bookmarks: Array.isArray(created?.bookmarks) ? created.bookmarks : [],
            }
            : item
        )));
      }
      setExpandedFolders((prev) => {
        const open = { ...prev };
        const wasOpen = open[tempId];
        delete open[tempId];
        if (created?.id) open[created.id] = wasOpen ?? true;
        return open;
      });
    } catch (err) {
      console.error("Couldn't create that folder.", err);
      if (actionGen.current === generation) {
        commitItems(previous);
      } else {
        commitItems(sidebarRef.current.filter((item) => item.id !== tempId));
      }
      setActionError("Couldn't create that folder.");
    }
  };

  const handleFolderRename = (folderId, newName) => runMutation(
    (items) => renameFolderInItems(items, folderId, newName),
    () => bookmarkFoldersApi.update(folderId, { name: newName }),
    "Couldn't save that folder name.",
  );

  const handleFolderDelete = (folderId, options = {}) => {
    const withBookmarks = options.withBookmarks === true;
    const folder = sidebarRef.current.find((item) => item.type === 'folder' && item.id === folderId);
    const localIds = (folder?.bookmarks || []).map((bookmark) => bookmark.id);
    return runMutation(
      (items) => (
        withBookmarks
          ? deleteFolderAndBookmarks(items, folderId)
          : deleteFolderToRoot(items, folderId)
      ),
      async () => {
        const result = withBookmarks
          ? await bookmarkFoldersApi.delete(folderId, { withBookmarks: true })
          : await bookmarkFoldersApi.delete(folderId);
        if (!withBookmarks) return;
        const returned = result?.deleted_bookmark_ids;
        releaseBookmarkPins(Array.isArray(returned) ? returned : localIds);
      },
      "Couldn't delete that folder.",
    );
  };

  const handleBookmarkDelete = (bookmarkId) => runMutation(
    (items) => removeBookmark(items, bookmarkId),
    async () => {
      await bookmarksApi.delete(bookmarkId);
      releaseBookmarkPins([bookmarkId]);
    },
    "Couldn't delete that bookmark.",
  );

  // Handle bookmark category change
  const handleBookmarkCategoryChange = (bookmarkId, newCategory) => {
    setSidebarItems(prev => prev.map(item => {
      if (item.type === 'bookmark' && item.id === bookmarkId) {
        return { ...item, category: newCategory };
      }
      if (item.type === 'folder' && item.bookmarks) {
        return {
          ...item,
          bookmarks: item.bookmarks.map(b =>
            b.id === bookmarkId ? { ...b, category: newCategory } : b
          )
        };
      }
      return item;
    }));
  };

  // Handle bookmark title change
  const handleBookmarkTitleChange = (bookmarkId, newTitle) => {
    setSidebarItems(prev => prev.map(item => {
      if (item.type === 'bookmark' && item.id === bookmarkId) {
        return { ...item, name: newTitle };
      }
      if (item.type === 'folder' && item.bookmarks) {
        return {
          ...item,
          bookmarks: item.bookmarks.map(b =>
            b.id === bookmarkId ? { ...b, name: newTitle } : b
          )
        };
      }
      return item;
    }));
  };

  // Move into a folder, or back to the top level when folderId is null.
  // The list changes before the save returns.
  const handleMoveBookmark = (bookmarkId, folderId) => {
    const target = folderId ?? null;
    if (target) {
      setExpandedFolders((prev) => ({ ...prev, [target]: true }));
    }
    return runMutation(
      (items) => relocateBookmark(items, bookmarkId, target),
      () => bookmarksApi.move(bookmarkId, target),
      "Couldn't move that bookmark.",
    );
  };

  const handleMoveBookmarkToRoot = (bookmarkId) => handleMoveBookmark(bookmarkId, null);

  const handleReorderBookmark = (bookmarkId, direction) => runMutation(
    (items) => reorderBookmarkInItems(items, bookmarkId, direction),
    () => bookmarksApi.reorder(bookmarkId, direction),
    "Couldn't change that order.",
  );

  const handleReorderFolder = (folderId, direction) => {
    const move = folderMoveIds(sidebarRef.current, folderId, direction);
    if (!move) return;
    return runMutation(
      (items) => reorderFolderInItems(items, folderId, direction),
      () => bookmarkFoldersApi.reorder(folderId, move.afterId, move.beforeId),
      "Couldn't change that order.",
    );
  };

  const folderChoices = sidebarItems
    .filter((item) => item.type === 'folder')
    .map((item) => ({ id: item.id, name: item.name }));

  const placementFor = (bookmarkId) => {
    const rootIds = sidebarItems
      .filter((item) => item.type === 'bookmark')
      .map((item) => item.id);
    const rootIndex = rootIds.indexOf(bookmarkId);
    if (rootIndex !== -1) {
      return { index: rootIndex, count: rootIds.length, folderId: null };
    }
    for (const item of sidebarItems) {
      if (item.type !== 'folder') continue;
      const ids = (item.bookmarks || []).map((bookmark) => bookmark.id);
      const index = ids.indexOf(bookmarkId);
      if (index !== -1) {
        return { index, count: ids.length, folderId: item.id };
      }
    }
    return { index: 0, count: 1, folderId: null };
  };

  // Handle create synthesis
  const handleCreateSynthesis = async (title, content, category) => {
    if (isProjectContext) {
      await bookmarksApi.createProjectSynthesis(projectId, {
        user_title: title,
        content,
        category
      });
    } else {
      await bookmarksApi.createSynthesis(storyId, {
        user_title: title,
        content,
        category
      });
    }
    setShowCreateModal(false);
    loadSidebar({ quiet: true });
  };

  // Filter items by category (for root bookmarks and folder bookmarks)
  const filterByCategory = (items) => {
    if (!activeCategory) return items;
    return items.map(item => {
      if (item.type === 'bookmark') {
        return item.category === activeCategory ? item : null;
      }
      if (item.type === 'folder') {
        const filteredBookmarks = (item.bookmarks || []).filter(b => b.category === activeCategory);
        if (filteredBookmarks.length === 0) return null;
        return { ...item, bookmarks: filteredBookmarks };
      }
      return item;
    }).filter(Boolean);
  };

  const filteredItems = filterByCategory(sidebarItems);
  const folders = filteredItems.filter(item => item.type === 'folder');
  const rootBookmarks = filteredItems.filter(item => item.type === 'bookmark');
  const allFolders = sidebarItems.filter((item) => item.type === 'folder');

  return (
    <div className="bookmarks-view">
      {/* Category Filters */}
      <div className="bookmarks-panel__filters">
        {CATEGORIES.map(cat => (
          <button
            key={cat.value || 'all'}
            className={`bookmarks-panel__filter ${activeCategory === cat.value ? 'bookmarks-panel__filter--active' : ''}`}
            onClick={() => setActiveCategory(cat.value)}
            style={cat.color && activeCategory === cat.value ? { borderColor: cat.color } : undefined}
          >
            {cat.label}
          </button>
        ))}
      </div>

      {/* Create Folder Button */}
      <div className="bookmarks-panel__actions">
        <CreateBookmarkFolderButton
          containerId={containerId}
          onCreate={handleFolderCreated}
        />
      </div>

      {/* Content */}
      <div className="bookmarks-panel__content">
        {actionError && !isLoading && (
          <p className="bookmarks-panel__action-error" role="alert">{actionError}</p>
        )}
        {isLoading ? (
          <div className="bookmarks-panel__loading">
            <Loading size="small" text="Loading..." />
          </div>
        ) : error ? (
          <div className="bookmarks-panel__error">
            <p>{error}</p>
            <button onClick={() => loadSidebar()}>Retry</button>
          </div>
        ) : filteredItems.length === 0 ? (
          <div className="bookmarks-panel__empty">
            <p>No bookmarks yet</p>
            <p className="bookmarks-panel__empty-hint">
              Bookmark messages from chat or create synthesis notes
            </p>
          </div>
        ) : (
          <div className="bookmarks-panel__list">
            {/* Folders first */}
            {folders.map(folder => {
              const folderIndex = allFolders.findIndex((item) => item.id === folder.id);
              return (
              <BookmarkFolderItem
                key={folder.id}
                folder={folder}
                bookmarks={folder.bookmarks || []}
                isExpanded={expandedFolders[folder.id] ?? false}
                onToggleCollapse={handleToggleFolderCollapse}
                onRename={handleFolderRename}
                onDelete={handleFolderDelete}
                onBookmarkDelete={handleBookmarkDelete}
                onBookmarkCategoryChange={handleBookmarkCategoryChange}
                onBookmarkTitleChange={handleBookmarkTitleChange}
                onMoveBookmarkToRoot={handleMoveBookmarkToRoot}
                folders={folderChoices}
                onMoveToFolder={handleMoveBookmark}
                onReorder={handleReorderBookmark}
                onReorderFolder={handleReorderFolder}
                canMoveUp={folderIndex > 0}
                canMoveDown={folderIndex !== -1 && folderIndex < allFolders.length - 1}
                bookmarkPlacement={placementFor}
              />
              );
            })}
            {/* Root bookmarks */}
            {rootBookmarks.map(bookmark => {
              const place = placementFor(bookmark.id);
              return (
              <BookmarkListItem
                key={bookmark.id}
                bookmark={{
                  id: bookmark.id,
                  user_title: bookmark.name,
                  content: bookmark.content,
                  category: bookmark.category,
                  bookmark_type: bookmark.bookmark_type,
                  session_name: bookmark.session_name,
                  created_at: bookmark.created_at
                }}
                onDelete={handleBookmarkDelete}
                onCategoryChange={handleBookmarkCategoryChange}
                onTitleChange={handleBookmarkTitleChange}
                folders={folderChoices}
                currentFolderId={null}
                onMoveToFolder={handleMoveBookmark}
                onReorder={handleReorderBookmark}
                canMoveUp={place.index > 0}
                canMoveDown={place.index < place.count - 1}
              />
              );
            })}
          </div>
        )}
      </div>

      {/* Footer with Create Button */}
      <div className="bookmarks-panel__footer">
        <button
          className="bookmarks-panel__create-btn"
          onClick={() => setShowCreateModal(true)}
        >
          + Create Synthesis
        </button>
      </div>

      {/* Create Synthesis Modal */}
      <CreateSynthesis
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSave={handleCreateSynthesis}
      />
    </div>
  );
}

export default BookmarksView;
